// Reads the authenticated user's onboarding signals from the device-connection
// "activated" endpoint (ENG-3796). Two signals ride ONE response:
//   - activated  — the user has ever added a camera ('added' event exists).
//     Permanent: once a camera is added, the record is never retracted.
//   - onboarded  — signal #3: the durable onboarding-complete flag. The server
//     computes onboarded = (users.onboarding_completed_at != NULL) OR (an
//     activation exists), so it SUBSUMES `activated` and also covers a user
//     who finished/skipped the wizard WITHOUT ever adding a camera.
// Per knowledge: onboarding-gate-signals (ai.knowledge/project) — both signals
// ride one fetch with no extra round-trip. The response is base64 protobuf
// decoded via the generated codec (see fetchActivation below for why).
// Per rule: reference-rules-in-comments, use-existing-protobuf-libraries (ai.knowledge/global).

import {
  DeviceConnectionActivatedRequest,
  DeviceConnectionActivatedResponse
} from '@tryiris-ai/common.protojs/api/v1/agent_config';

/** The onboarding signals the gate consumes from a single activated fetch. */
export interface ActivationSignals {
  /** DB says this user has added a camera at some point (permanent). */
  readonly activated: boolean;
  /** Durable onboarding-complete flag; subsumes `activated`. Gate signal #3. */
  readonly onboarded: boolean;
}

// Base64 for proto request bytes, same as mailroom-helper/requester.ts (not exported there).
// Chunked because spreading one argument per byte overflows the stack; 0x8000 is safe even deep.
function toBase64(bytes: Uint8Array): string {
  if (typeof globalThis.btoa === 'function') {
    const CHUNK_SIZE = 0x8000;
    let binary = '';
    for (let offset = 0; offset < bytes.length; offset += CHUNK_SIZE) {
      binary += String.fromCodePoint(...bytes.subarray(offset, offset + CHUNK_SIZE));
    }
    return globalThis.btoa(binary);
  }
  // Node fallback when btoa is absent.
  return Buffer.from(bytes).toString('base64');
}

/**
 * Query the DB for this user's onboarding signals (activated + onboarded) in
 * one round-trip. This is the SINGLE fetch the gate depends on, so a pending
 * call keeps BOTH signals unresolved together (see gate.tsx).
 *
 * @throws If the network request fails, the server returns non-200, or the body
 *   carries an api error. The gate records a throw as "unknown"; the caller must
 *   NOT silently swallow here.
 */
// The service path prefix is REQUIRED. `config.apiEndpoint` is the bare host
// (https://api.itg.tryiris.ai/), and posting to `<host>/rpc/...` returns 403 for
// EVERY route — not just this one — because the gateway only maps rpc paths
// under the owning service. Verified against ITG: bare host 403s on activated,
// history, and config.get alike; the prefixed path resolves.
const AGENT_CONFIG_BASE = 'api/v1/agent-config/';

export const fetchActivation = async (
  token: string,
  apiEndpoint: string
): Promise<ActivationSignals> => {
  const url = `${apiEndpoint}${AGENT_CONFIG_BASE}rpc/api.authenticated.v2.agent-config.device-connection.activated`;

  // Send proto-base64 so the mailroom answers in base64 (response.go marshalHTTP).
  // A JSON request gets raw binary protobuf back, and the Lambda's string body
  // mangles every byte >= 0x80, so an error code of 401 decodes as garbage.
  // Per rule: use-existing-protobuf-libraries (ai.knowledge/global) — generated codec only.
  const requestBytes = DeviceConnectionActivatedRequest.encode(
    DeviceConnectionActivatedRequest.fromPartial({ token })
  ).finish();

  const response = await fetch(url, {
    body: toBase64(requestBytes),
    headers: { 'Content-Type': 'application/proto-base64' },
    method: 'POST'
  });

  if (!response.ok) {
    throw new Error(`activated endpoint returned ${response.status}`);
  }

  const responseBytes = new Uint8Array(
    [...atob(await response.text())].map((c) => c.codePointAt(0)!)
  );
  const data = DeviceConnectionActivatedResponse.decode(responseBytes);

  // The mailroom returns handler errors (expired token, DB failure) as HTTP 200
  // with the error in field 1 and every other field zero. Throw so the gate
  // records "unknown" instead of a confirmed brand-new user; it then settles on
  // bridge-ready or its ceiling like any other failed fetch.
  // Per rule: absent-data-gates-permissively (ai.knowledge/global) — an error is not "not onboarded".
  if (data.error !== undefined) {
    throw new Error(
      `activated endpoint returned api error ${data.error.code}: ${data.error.message}`
    );
  }

  const activated = data.activated === true;
  // Older deployments do not return `onboarded`. Activation already means
  // onboarding is complete, so preserve that established-user signal while
  // the API and summary build roll out independently.
  return { activated, onboarded: activated || data.onboarded === true };
};

/**
 * Backward-compatible boolean read of the activation signal. Retained so the
 * existing "has this user ever added a camera?" call sites and tests keep a
 * single-purpose entry point; delegates to {@link fetchActivation}.
 *
 * @throws If the network request fails or the server returns non-200.
 */
export const fetchHasEverAddedCamera = async (
  token: string,
  apiEndpoint: string
): Promise<boolean> => {
  const { activated } = await fetchActivation(token, apiEndpoint);
  return activated;
};
